import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import types from '../actions/types'
import {generalError} from '../actions'
import {createGoogleCompanyDocumentWatcher, createPivotTableWatcher} from 'sagas/pivotTable'
import {fullDiaryFields} from 'sagas/diarySaga'
import {get, isEmpty, isNil} from 'lodash'
import {quotePivotTableFields} from 'containers/quotePivotTable'
import {invoicePivotTableFields} from 'sagas/invoicingSaga'
import {default as pivotTableActions, requestData} from 'actions/pivotTable'
import {snackbarMessage} from 'actions/snackbar'
import {getFileFormConfig, getFileFormData} from 'helpers/documents'
import apiHelper from 'helpers/api'
import {opportunityFields} from 'sagas/opportunitySaga'
import {getAddExternalUserDialog, getAddUserDialog, getViewCompanyDialogCurrentFolder} from 'selectors/dialog'
import {getExistingUserError} from 'helpers/user'
import {getExternalUsers, getInternalUsers} from 'selectors/settingsContext'
import {loadAccountLicenceInfo} from 'actions/settings'
import {getCompanyFields, getContactFields} from 'helpers/contacts'
import {
  viewCompanyActivitiesPivotTableKey,
  viewCompanyDocumentsPivotTableKey,
  viewCompanyInvoicesPivotTableKey,
  viewCompanyOpportunitiesPivotTableKey,
  viewCompanyQuotesPivotTableKey,
} from 'reducers/dialogs/viewCompanyDialog'
import {
  hideEditActivityDialog,
  loadActivityFailure,
  loadActivitySuccess,
  showEditActivityDialog,
  updateAddUserDialog,
  updateExternalAddUserDialog,
  viewCompanyDialogAddFolder,
  viewCompanyDialogCompanyLoad,
  viewCompanyDialogCompanyLoadFailure,
  viewCompanyDialogCompanyLoadSuccess,
  viewCompanyDialogContactLoad,
  viewCompanyDialogContactLoadFailure,
  viewCompanyDialogContactLoadSuccess,
  viewCompanyDialogLoadDocuments,
  viewCompanyDialogLoadParentFolderFailure,
  viewCompanyDialogLoadParentFolderSuccess,
  viewCompanyDialogRemoveFolderAfterIndex,
  viewCompanyDialogSetFoldersToRootDirectory,
  viewCompanyDialogUploadDocumentsFailure,
} from 'actions/dialog'

export default (api) => {

  function* loadActivity({id}) {
    try {
      const fields = {
        default: true,
        company: true,
        contact: true,
        order: true,
        opportunity: true,
        owner: {
          default: true,
          contact: true,
        },
        attendees: true,
        activityUpdates: {
          default: true,
          previousOwnerId: true,
          previousOwner: {
            forename: true,
            surname: true,
          },
          attendeeChanges: {
            default: true,
            contact: {
              forename: true,
              surname: true,
            },
          },
          contact: {
            fullName: true,
          },
        },
        activityHistory: {
          default: true,
          contact: {
            fullName: true,
          },
        },
      }

      const response = yield call(api.activity.getOne, id, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const activity = data.result

            yield put(loadActivitySuccess(activity))
            yield put(showEditActivityDialog(activity))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(`Unfortunately, activity with id ${id} could not be loaded.`))
            yield put(loadActivityFailure(message, code))
            yield put(hideEditActivityDialog())
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  const getPivotTableStateParams = (state) => {
    const companyId = get(state, 'dialogs.viewCompany.company.id')
    const contactId = get(state, 'dialogs.viewCompany.contact.id')

    const params = {}
    if (!isNil(companyId)) params['companyId'] = companyId
    if (!isNil(contactId)) params['contactId'] = contactId

    return params
  }

  const viewCompanyActivitiesPivotTable = createPivotTableWatcher(
      api.activity,
      viewCompanyActivitiesPivotTableKey,
      [
        types.VIEW_COMPANY_DIALOG_ACTIVITIES_UPDATE_FILTERS,
        types.DIARY_ADD_ACTIVITY_SUCCESS,
        types.DIARY_EDIT_ACTIVITY_SUCCESS,
        types.DIARY_DELETE_ACTIVITY_SUCCESS,
      ],
      state => {
        const params = getPivotTableStateParams(state)
        params['includeCompleted'] = state.dialogs.viewCompany.showCompletedActivities
        params['includeRejected'] = state.dialogs.viewCompany.showRejectedActivities

        return params
      },
      fullDiaryFields
  )

  const viewCompanyOpportunitiesPivotTable = createPivotTableWatcher(
      api.opportunity,
      viewCompanyOpportunitiesPivotTableKey,
      [],
      state => {
        return {
          ...getPivotTableStateParams(state),
          summary: true,
        }
      },
      opportunityFields
  )

  const viewCompanyDocumentsPivotTable = createGoogleCompanyDocumentWatcher(
      api.documents,
      viewCompanyDocumentsPivotTableKey,
      [
        types.VIEW_COMPANY_DIALOG_DOCUMENTS_UPDATE_FILTERS,
      ],
      state => {
        return {
          folderId: get(state, 'dialogs.viewCompany.currentFolder.id'),
          companyId: get(state, 'dialogs.viewCompany.company.id'),
        }
      }
  )

  const viewCompanyQuotesPivotTable = createPivotTableWatcher(
      api.quote,
      viewCompanyQuotesPivotTableKey,
      [
        types.VIEW_COMPANY_DIALOG_QUOTES_UPDATE_FILTERS,
        types.QUOTING_QUOTE_ADD_SUCCESS,
      ],
      state => {
        const params = getPivotTableStateParams(state)
        params['allRevisions'] = state.dialogs.viewCompany.showAllQuotingRevisions

        return params
      },
      quotePivotTableFields
  )

  const viewCompanyInvoicesPivotTable = createPivotTableWatcher(
      api.invoice,
      viewCompanyInvoicesPivotTableKey,
      [
        types.VIEW_COMPANY_DIALOG_INVOICES_UPDATE_FILTERS,
      ],
      state => {
        const params = getPivotTableStateParams(state)
        params['showPosted'] = state.dialogs.viewCompany.showPostedInvoices

        return params
      },
      invoicePivotTableFields
  )

  function* showViewCompanyDialog({companyId}) {
    yield put(viewCompanyDialogCompanyLoad(companyId))
  }

  function* showViewCompanyDialogForContact({contactId}) {
    yield put(viewCompanyDialogContactLoad(contactId))
  }

  function* selectedContact({contactId}) {
    yield put(viewCompanyDialogContactLoad(contactId))
  }

  function getPivotTableKeyFor(tab) {
    switch (tab) {
      case 'activities':
        return viewCompanyActivitiesPivotTableKey
      case 'documents':
        return viewCompanyDocumentsPivotTableKey
      case 'quotes':
        return viewCompanyQuotesPivotTableKey
      case 'invoices':
        return viewCompanyInvoicesPivotTableKey
      default:
        return viewCompanyActivitiesPivotTableKey
    }
  }

  function* loadCompany({companyId}) {
    try {
      const response = yield call(api.company.getOne, companyId, getCompanyFields())
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            let company = data.result
            company = apiHelper.companyInbound(company)
            yield put(viewCompanyDialogCompanyLoadSuccess(company))
            const selectedTab = yield select(state => state.dialogs.viewCompany.selectedTab)
            yield put(requestData(getPivotTableKeyFor(selectedTab)))
          },
          function* onFail(key, message, code) {
            yield put(viewCompanyDialogCompanyLoadFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadContact({contactId}) {
    try {
      const response = yield call(api.contact.getOne, contactId, getContactFields())
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            let contact = data.result
            contact = apiHelper.contactInbound(contact)
            yield put(viewCompanyDialogContactLoadSuccess(contact))
            const selectedTab = yield select(state => state.dialogs.viewCompany.selectedTab)
            yield put(requestData(getPivotTableKeyFor(selectedTab)))
          },
          function* onFail(key, message, code) {
            yield put(viewCompanyDialogContactLoadFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadDocuments({folder}) {
    try {
      const key = viewCompanyDocumentsPivotTableKey
      yield put(pivotTableActions.loadData(key, {}))
      const response = yield call(api.documents.getDocuments, folder.id)
      if (response.ok) {
        const rootFolder = yield select(state => state.dialogs.viewCompany.rootFolder)
        const folders = yield select(state => state.dialogs.viewCompany.folders)

        // Check if we have opened any folders yet
        if (isEmpty(folders)) {
          yield put(viewCompanyDialogAddFolder(folder))
        } else {
          // If the folder id isn't the same as the folder we have selected add it to the list
          // else load the root directory
          if (rootFolder.id !== folder.id) {
            // Find we are navigating to previous folder remove all the folders after the one we are navigating to
            // else add the folder to the list
            if (folders.filter(function (f) {
              return f.id === folder.id
            }).length > 0) {
              const currentIndex = 1
              const index = folders.findIndex(f => f.id === folder.id) + currentIndex
              yield put(viewCompanyDialogRemoveFolderAfterIndex(index))
            } else {
              yield put(viewCompanyDialogAddFolder(folder))
            }
          } else {
            yield put(viewCompanyDialogSetFoldersToRootDirectory(folder))
          }
        }

        const count = get(response, 'data.data.length', 0)
        const data = get(response, 'data.data', [])
        yield put(pivotTableActions.loadDataSuccess(key, data, count))
      } else {
        yield put(pivotTableActions.loadDataFailure(key, response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDocumentsParentFolder({companyId}) {
    try {
      const response = yield call(api.documents.getCompanyDocumentsFolder, companyId)
      if (response.ok) {
        const data = response.data
        yield put(viewCompanyDialogLoadParentFolderSuccess(data))
        const currentFolder = yield select(state => getViewCompanyDialogCurrentFolder(state))
        yield put(viewCompanyDialogLoadDocuments(currentFolder))
      } else {
        yield put(viewCompanyDialogLoadParentFolderFailure(response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteDocuments({documentIds}) {
    try {
      yield put(snackbarMessage('Deleting your files...', 'loading', false, false))
      const response = yield call(api.documents.delete, documentIds)
      if (response.ok) {
        const currentFolder = yield select(state => getViewCompanyDialogCurrentFolder(state))
        yield put(viewCompanyDialogLoadDocuments(currentFolder))
        const failedFiles = response.data.data.failed
        if (!isEmpty(failedFiles)) {
          const failedQty = failedFiles.length
          yield put(snackbarMessage(`There was an error deleting ${failedQty} of your files`, 'error', false))
        } else {
          yield put(snackbarMessage('Your files have been deleted', 'checkmark'))
        }
      } else {
        yield put(snackbarMessage('Your files could not be deleted: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* uploadDocuments({documents}) {
    try {
      yield put(snackbarMessage('Uploading your files...', 'loading', false, false))
      // Get the current selected folder to send the data to
      const currentFolder = yield select(state => getViewCompanyDialogCurrentFolder(state))
      const response = yield call(
          api.documents.uploadDocuments,
          currentFolder.id,
          getFileFormData(documents),
          getFileFormConfig()
      )
      if (response.ok) {
        yield put(viewCompanyDialogLoadDocuments(currentFolder))
        yield put(snackbarMessage('Your files have been uploaded', 'checkmark'))
      } else {
        yield put(viewCompanyDialogUploadDocumentsFailure(response.message, response.code))
        yield put(snackbarMessage('Your files could not be uploaded: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* updateUserContact({data}) {
    const internalUsers = Object.values(yield select(getInternalUsers))
    const externalUsers = Object.values(yield select(getExternalUsers))
    const addUserDialog = yield select(getAddUserDialog)
    const isAddUserDialogVisible = get(addUserDialog, 'isVisible', false)

    const company = get(data, 'company', null)
    const contact = {...data}
    delete contact.company

    const contactMetaData = {
      contact,
      company,
      errorText: getExistingUserError(internalUsers, externalUsers, contact),
    }

    if (isAddUserDialogVisible)
      yield put(updateAddUserDialog(contactMetaData))

    const addExternalUserDialog = yield select(getAddExternalUserDialog)
    const isAddExternalDialogVisible = get(addExternalUserDialog, 'isVisible', false)
    if (isAddExternalDialogVisible)
      yield put(updateExternalAddUserDialog(contactMetaData))

    const users = internalUsers.concat(externalUsers)
    const userContactIds = users.map(user => user.contactId)

    const contactId = get(contact, 'id', null)
    const shouldRefreshAccountLicenceInfo = (
        (!isAddUserDialogVisible && !isAddExternalDialogVisible) &&
        userContactIds.includes(contactId)
    )

    if (shouldRefreshAccountLicenceInfo)
      yield put(loadAccountLicenceInfo())
  }

  function* watcher() {
    yield all([
      takeLatest(types.DIALOG_LOAD_ACTIVITY_ATTEMPT, loadActivity),
      takeLatest(types.SHOW_VIEW_COMPANY_DIALOG, showViewCompanyDialog),
      takeLatest(types.SHOW_VIEW_COMPANY_DIALOG_FOR_CONTACT, showViewCompanyDialogForContact),
      takeLatest(types.VIEW_COMPANY_DIALOG_SELECT_CONTACT, selectedContact),
      takeLatest(types.VIEW_COMPANY_DIALOG_COMPANY_LOAD_ATTEMPT, loadCompany),
      takeLatest(types.VIEW_COMPANY_DIALOG_CONTACT_LOAD_ATTEMPT, loadContact),
      takeLatest(types.VIEW_COMPANY_DIALOG_LOAD_DOCUMENTS, loadDocuments),
      takeLatest(types.VIEW_COMPANY_DIALOG_DOCUMENTS_LOAD_PARENT_FOLDER_ATTEMPT, loadDocumentsParentFolder),
      takeLatest(types.VIEW_COMPANY_DIALOG_DELETE_DOCUMENTS_ATTEMPT, deleteDocuments),
      takeLatest(types.VIEW_COMPANY_DIALOG_UPLOAD_DOCUMENTS_ATTEMPT, uploadDocuments),
      takeLatest(types.CONTACTS_CONTACT_EDIT_SUCCESS, updateUserContact),

      yield fork(viewCompanyActivitiesPivotTable),
      yield fork(viewCompanyOpportunitiesPivotTable),
      yield fork(viewCompanyDocumentsPivotTable),
      yield fork(viewCompanyQuotesPivotTable),
      yield fork(viewCompanyInvoicesPivotTable),
    ])
  }

  return {
    watcher,
  }
}
